import type { ReactElement } from 'react';
import { useEffect, useRef, useState } from 'react';
import type { VersionListResponse } from '@shared/api';
import { listVersions } from '../api/client';
import { formatDateTime } from '../lib/format';
import { hasHiddenSystemVersions, visibleVersions } from '../lib/versionsMenu';
import { HistoryIcon } from './icons';

/** Version history, undo and revert. Small on purpose — it is a safety net. */
export function VersionsMenu({
  proposalId,
  currentVersion,
  onUndo,
  onRevert,
  busy,
}: {
  proposalId: string;
  currentVersion: number;
  onUndo: () => void;
  onRevert: (version: number) => void;
  busy: boolean;
}): ReactElement {
  const [open, setOpen] = useState(false);
  const [versions, setVersions] = useState<VersionListResponse['versions']>([]);
  const [error, setError] = useState<string | null>(null);
  // docs/simplify.md §2g: hides changeOrigin 'system' rows (agent
  // housekeeping) by default — the rep can reveal them with this toggle.
  const [showAll, setShowAll] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const shown = visibleVersions(versions, showAll);
  const canShowAll = hasHiddenSystemVersions(versions);

  useEffect(() => {
    if (!open) return;
    let live = true;
    void listVersions(proposalId)
      .then((res) => {
        if (live) {
          setVersions(res.versions);
          setError(null);
        }
      })
      .catch((cause: unknown) => {
        if (live) setError(cause instanceof Error ? cause.message : String(cause));
      });
    return () => {
      live = false;
    };
  }, [open, proposalId, currentVersion]);

  useEffect(() => {
    if (!open) return;
    const onDown = (event: MouseEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="menu" ref={root}>
      <button
        type="button"
        className="workspace-action-button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Open version history. Current version ${currentVersion}`}
        title={`Version history (v${currentVersion})`}
      >
        <HistoryIcon />
      </button>
      {open ? (
        <div className="menu-panel" role="menu">
          <button
            type="button"
            className="menu-item menu-item-strong"
            disabled={busy}
            onClick={() => {
              setOpen(false);
              onUndo();
            }}
          >
            Undo last change
          </button>
          <div className="menu-divider" />
          {error ? <div className="menu-empty">{error}</div> : null}
          {!error && shown.length === 0 ? <div className="menu-empty">No history yet.</div> : null}
          {canShowAll ? (
            <label className="menu-item menu-toggle">
              <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} />
              Show all
            </label>
          ) : null}
          <ul className="menu-list">
            {shown.map((version) => (
              <li key={version.version}>
                <div className="menu-version">
                  <span className="menu-version-n">v{version.version}</span>
                  <span className="menu-version-summary">{version.changeSummary}</span>
                  <span className="menu-version-meta">
                    {version.changedBy}
                    {formatDateTime(version.createdAt) ? ` · ${formatDateTime(version.createdAt)}` : ''}
                  </span>
                </div>
                {version.version === currentVersion ? (
                  <span className="menu-current">current</span>
                ) : (
                  <button
                    type="button"
                    className="linkish"
                    disabled={busy}
                    onClick={() => {
                      setOpen(false);
                      onRevert(version.version);
                    }}
                  >
                    Revert
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
